/*
    Merrid Controls. Wszelkie prawa zastrzeżone.
    Plik zawiera kody Źródłowe będące własnością firmy Merrid Controls.
    Nie wolno ich rozpowszechniać w całości lub fragmentach bez wyraźnej zgody właściciela.
*/


import React, {useContext, useState, useEffect} from "react";

import useTypedSelector from "@Services/Hooks/UseTypedSelector";

import ApplicationApiContext from "@Contexts/Api";


import LOGGED_USER_INTERFACE from "@Store/Interfaces/Authentication/User";


import ERROR_SYSTEM_MESSAGE_INTERFACE from "@Services/Application/Interfaces/_Messages/Errors";


import {Col, Form, Row} from "react-bootstrap";


import ErrorMessageComponent from "@Components/UI/Communicates/Messages/Error";


import MONITORY_NOTE_INTERFACE from "@Services/Application/Interfaces/MonitoryNotes/MonitoryNote";

import {HTTP_ERROR_RESPONSE_INTERFACE} from "@Services/Interfaces/Http/Response/Error";
import AUTHORIZATION_TOKEN_INTERFACE from "@Reducers/Api/RequestToken";
import REST_API_REQUEST from "@Services/Utilities/Requests";
import CHANGE_API_REQUEST_TOKEN_ACTION from "@Actions/Api/ChangeApiRequestTokenValue";
import prepareRestApiRequestToken from "@Services/Utilities/RequestToken/Prepare";
import ContentInformationComponent from "@Components/UI/Content/Information";


type MonitoryNoteScheduleWindowProps = {
    MONITORY_NOTE: MONITORY_NOTE_INTERFACE | null;
    MONITORY_NOTE_HANDLER?(MONITORY_NOTE: MONITORY_NOTE_INTERFACE): void;
    MONITORY_NOTE_ACTION_TYPE?: "edition" | "creation" | "submit" | "delete";
    EDITABLE?: boolean;
    LOAD_CONTENT?: boolean;
    SCHEDULE_WINDOW_LOADED_STATUS_HANDLER(status: boolean): void;
};


export const MonitoryNoteScheduleWindowComponent: React.FC<MonitoryNoteScheduleWindowProps> = ({
    MONITORY_NOTE, MONITORY_NOTE_HANDLER = null,
    MONITORY_NOTE_ACTION_TYPE = "edition",
    EDITABLE = true,
    LOAD_CONTENT = true,
    SCHEDULE_WINDOW_LOADED_STATUS_HANDLER
}): JSX.Element | null => {


    const LOGGED_USER: LOGGED_USER_INTERFACE = useTypedSelector(
        (state):ReturnType<any> => state?.authentication
    )?.loggedUser || null;


    const REST_API_URL = useContext(ApplicationApiContext)?.API_ROOT_URL || null;


    /** @const {object} REST_API_DATA - Application State Store Rest Api Data */
    const REST_API_DATA:ReturnType<any> = useTypedSelector(state => state.api);

    /** @const {} REST_API_REQUEST_TOKEN - Application Rest Api Request Token */
    const REST_API_REQUEST_TOKEN: AUTHORIZATION_TOKEN_INTERFACE = REST_API_DATA?.requestToken || null;


    const [MONITORY_NOTE_SCHEDULE_WINDOW, SET_MONITORY_NOTE_SCHEDULE_WINDOW] = useState<any>(null);
    const [MONITORY_NOTE_SCHEDULE_WINDOW_ERROR, SET_MONITORY_NOTE_SCHEDULE_WINDOW_ERROR] = useState<ERROR_SYSTEM_MESSAGE_INTERFACE | null>(null);

    useEffect(() => {

        if (!MONITORY_NOTE) return;

        if (MONITORY_NOTE_SCHEDULE_WINDOW) return;

        const GET_MONITORY_NOTE_SCHEDULE_WINDOW = async () => {

            const SCHEDULE_WINDOW_REQUEST_BODY: any = {
                UserId: LOGGED_USER?.userID || null,
                TerminalNo: MONITORY_NOTE.TerminalNo,
                FrontTruckRegNo: MONITORY_NOTE.FrontTruckRegNo || null,
                RearTruckRegNo: MONITORY_NOTE.RearTruckRegNo || null,
                Disps: MONITORY_NOTE.Disps
            };

            if (MONITORY_NOTE_ACTION_TYPE !== "creation") SCHEDULE_WINDOW_REQUEST_BODY["TransportNotificationNo"] = MONITORY_NOTE?.TransportNotificationNo;

            try {

                const SCHEDULE_WINDOW_API_URL: string = REST_API_URL + "/TransportNotification/Schedule";

                const SCHEDULE_WINDOW_RESPONSE = await REST_API_REQUEST(
                    SCHEDULE_WINDOW_API_URL,
                    SCHEDULE_WINDOW_REQUEST_BODY,
                    REST_API_REQUEST_TOKEN
                );

                if (SCHEDULE_WINDOW_RESPONSE.status === 200) {

                    const CONTENT: any = await SCHEDULE_WINDOW_RESPONSE.text();
                    SET_MONITORY_NOTE_SCHEDULE_WINDOW(CONTENT.trim() ? JSON.parse(CONTENT)?.Schedules : []);

                    if (!!SCHEDULE_WINDOW_LOADED_STATUS_HANDLER) SCHEDULE_WINDOW_LOADED_STATUS_HANDLER(true);

                } else {
                    const CONTENT: HTTP_ERROR_RESPONSE_INTERFACE = await SCHEDULE_WINDOW_RESPONSE.json();
                    SET_MONITORY_NOTE_SCHEDULE_WINDOW_ERROR({ErrorCode: SCHEDULE_WINDOW_RESPONSE.status, UserMessage: CONTENT.UserMessage});
                }

            } catch (e) {
                SET_MONITORY_NOTE_SCHEDULE_WINDOW_ERROR({ErrorCode: 500, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
            }

        };

        GET_MONITORY_NOTE_SCHEDULE_WINDOW();

    }, [MONITORY_NOTE?.Disps]);


    const SCHEDULE_DATE = MONITORY_NOTE?.ProcessStartDate?.slice(0, 10) || "0";
    const SCHEDULE_ID = MONITORY_NOTE?.ScheduleId || "";

    const [selectedScheduleDate, setSelectedScheduleDate] = useState<string>(SCHEDULE_DATE);
    const [selectedScheduleId, setSelectedScheduleId] = useState<string>(SCHEDULE_ID);

    const changeSelectedScheduleDate = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedScheduleDate = e.target.value;
        setSelectedScheduleId("0");
        setSelectedScheduleDate(selectedScheduleDate);
        if (MONITORY_NOTE_HANDLER) {
            MONITORY_NOTE_HANDLER({...MONITORY_NOTE, ScheduleId: null, IsBooked: false, ProcessStartDate: selectedScheduleDate});         
        }
    };

    const changeSelectedScheduleId = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedScheduleId = e.target.value;
        setSelectedScheduleId(selectedScheduleId);
        if (MONITORY_NOTE_HANDLER) {
            MONITORY_NOTE_HANDLER({...MONITORY_NOTE, ScheduleId: selectedScheduleId, IsBooked: !!selectedScheduleId});
        }
    };

    useEffect(() => {
        return () => {
            SET_MONITORY_NOTE_SCHEDULE_WINDOW(null);
            SET_MONITORY_NOTE_SCHEDULE_WINDOW_ERROR(null);
        };
    }, []);


    if (!LOAD_CONTENT) return null;


    if (!MONITORY_NOTE_SCHEDULE_WINDOW) return null;


    if (MONITORY_NOTE_SCHEDULE_WINDOW_ERROR) return <ErrorMessageComponent messageText={MONITORY_NOTE_SCHEDULE_WINDOW_ERROR?.UserMessage} />;

    const SCHEDULE_WINDOW_LIST = MONITORY_NOTE_SCHEDULE_WINDOW.map(
        (schedule: any) => [schedule?.Date, {scheduleId: schedule?.ScheduleId, scheduleTime: schedule?.Time}]
    );

    const SCHEDULE_WINDOW: any = {};

    for (let [scheduleDate, scheduleData] of SCHEDULE_WINDOW_LIST) {
        if (!SCHEDULE_WINDOW[scheduleDate]) SCHEDULE_WINDOW[scheduleDate] = [];
        SCHEDULE_WINDOW[scheduleDate].push(scheduleData);
    }

    const SCHEDULE_WINDOW_DATES = Object.keys(SCHEDULE_WINDOW);
    const ScheduleWindowDates = SCHEDULE_WINDOW_DATES.map((scheduleDate) =>
        <option key={scheduleDate} value={scheduleDate}>{scheduleDate}</option>
    );

    const SCHEDULE_WINDOW_TIMES = SCHEDULE_WINDOW[selectedScheduleDate] || [];
    const ScheduleWindowTimes = SCHEDULE_WINDOW_TIMES.map((schedule: any) =>
        <option key={schedule.scheduleId} value={schedule.scheduleId}>{schedule.scheduleTime}</option>
    );


    return (
        <Col xs={12} className={"monitory-note-window-schedule monitory-note-window-schedule-component"}>
            <Row className={"monitory-note-window-schedule-content"}>

                <Col xs={12} className={"window-schedule-header monitory-note-section-header"}>
                    <span>{`Okno czasowe`}</span>
                </Col>

                {!!MONITORY_NOTE_SCHEDULE_WINDOW && MONITORY_NOTE_SCHEDULE_WINDOW.length > 0 ?
                <Col xs={12} className={"note-window-schedule"}>
                    <Row className={"note-window-schedule-content"}>

                    <Col xs={12} className={"window-schedule-field"}>
                        <Form.Control as={"select"} value={selectedScheduleDate} onChange={changeSelectedScheduleDate} disabled={!EDITABLE}>
                            <option value={"0"}>- wybierz datę -</option>
                            {ScheduleWindowDates}
                        </Form.Control>
                    </Col>

                    {selectedScheduleDate !== "0" && SCHEDULE_WINDOW_DATES.includes(selectedScheduleDate) &&
	                <Col xs={12} className={"window-schedule-field"}>
		                <Form.Control as={"select"} value={selectedScheduleId} onChange={changeSelectedScheduleId} disabled={!EDITABLE}>
                            <option value={""}>- wybierz godzinę -</option>
                            {ScheduleWindowTimes}
		                </Form.Control>
	                </Col>
                    }

                    </Row>

                </Col>
                :
                    <ContentInformationComponent contentMessage={"Brak okien czasowych"} />
                }

            </Row>
        </Col>
    );

};


export default MonitoryNoteScheduleWindowComponent;
